import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Shield, FileText, AlertTriangle } from 'lucide-react';

// Renders a translated array of { label, text } items as "<strong>label:</strong> text"
const LabeledItems = ({ items }) => (
  <>
    {items.map((item, i) => (
      <li key={i}>
        <strong className="text-gray-900 dark:text-white">{item.label}{item.label.endsWith('.') ? '' : ':'}</strong> {item.text}
      </li>
    ))}
  </>
);

// Renders a translated array of plain strings as <li> items
const PlainItems = ({ items }) => (
  <>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </>
);

// Shared Layout for Legal Pages
const LegalPageLayout = ({ children, title, icon: Icon, lastUpdated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      className="w-full min-h-screen relative overflow-hidden bg-white dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
        :root {
          --legal-nav-bg: rgba(255,255,255,0.7);
          --legal-nav-border: rgba(0,0,0,0.08);
          --legal-card-bg: rgba(0,0,0,0.03);
          --legal-card-border: rgba(0,0,0,0.08);
        }
        .dark {
          --legal-nav-bg: rgba(9,9,15,0.7);
          --legal-nav-border: rgba(255,255,255,0.08);
          --legal-card-bg: rgba(255,255,255,0.03);
          --legal-card-border: rgba(255,255,255,0.08);
        }
      `}</style>

      {/* Top radial glow — identical to the homepage hero */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 900,
          height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Top Nav */}
      <nav className="z-10 backdrop-blur-xl border-b sticky top-0" style={{ background: 'var(--legal-nav-bg)', borderColor: 'var(--legal-nav-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors flex-shrink-0"
            aria-label={t('legal.layout.goBackAria')}
          >
            <ArrowLeft size={20} />
            <span>{t('legal.layout.back')}</span>
          </button>

          {/* Contact-variant links + Register are reachable from the Contact
              page itself and this page's own footer cross-links, so on
              narrow screens only Back/Home stay in the nav -- 7 unwrapped
              items here overflowed the viewport badly at 375px (found live
              during the 2026-07-17 mobile audit). */}
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
              {t('legal.layout.home')}
            </button>
            <div className="hidden sm:flex items-center gap-4">
              <button onClick={() => navigate('/contact?type=sales')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
                {t('legal.layout.contactSales')}
              </button>
              <button onClick={() => navigate('/contact?type=legal')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
                {t('legal.layout.contactLegal')}
              </button>
              <button onClick={() => navigate('/contact?type=technical')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
                {t('legal.layout.contactTechnical')}
              </button>
              <button onClick={() => navigate('/contact?type=general')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
                {t('legal.layout.contactGeneral')}
              </button>
              <button onClick={() => navigate('/register')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap">
                {t('legal.layout.register')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        <div className="rounded-3xl p-8 md:p-12 shadow-2xl" style={{ background: 'var(--legal-card-bg)', border: '1px solid var(--legal-card-border)' }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-violet-50 dark:bg-violet-500/20 p-4 rounded-xl border border-violet-300 dark:border-violet-500/30">
              <Icon className="text-violet-600 dark:text-violet-400" size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">{title}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('legal.layout.lastUpdatedPrefix')} {lastUpdated || t('legal.layout.lastUpdatedDate')}</p>
            </div>
          </div>

          <div className="prose dark:prose-invert prose-violet max-w-none">
            {children}
          </div>
        </div>

        {/* Footer Nav */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <button onClick={() => navigate('/privacy')} className="text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
            {t('legal.layout.footerPrivacy')}
          </button>
          <span className="text-gray-600">•</span>
          <button onClick={() => navigate('/terms')} className="text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
            {t('legal.layout.footerTerms')}
          </button>
          <span className="text-gray-600">•</span>
          <button onClick={() => navigate('/disclaimer')} className="text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
            {t('legal.layout.footerDisclaimer')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Privacy ----------
export const PrivacyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const p = 'legal.privacy';

  return (
    <LegalPageLayout title={t(`${p}.pageTitle`)} icon={Shield}>
      <Helmet>
        <title>Privacy Policy | Stocker Beta</title>
        <meta name="description" content="How Stocker collects, uses, and protects your data -- account info, usage data, retention, and your rights under GDPR." />
        <link rel="canonical" href="https://stocker-beta.com/privacy" />
      </Helmet>
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s1.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s1.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s2.title`)}</h2>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-6">{t(`${p}.s2.accountInfo.title`)}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s2.accountInfo.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s2.accountInfo.items`, { returnObjects: true })} />
        </ul>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-6">{t(`${p}.s2.usageData.title`)}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s2.usageData.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s2.usageData.items`, { returnObjects: true })} />
        </ul>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-6">{t(`${p}.s2.technicalData.title`)}</h3>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s2.technicalData.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s3.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s3.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s3.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s4.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s4.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s4.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s5.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          <strong className="text-gray-900 dark:text-white">{t(`${p}.s5.introBold`)}</strong> {t(`${p}.s5.introRest`)}
        </p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s5.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s6.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s6.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s6.items`, { returnObjects: true })} />
        </ul>

        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">
          {t(`${p}.s6.exercisePrefix`)}{' '}
          <button
            onClick={() => navigate('/contact?type=legal')}
            className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 underline underline-offset-4"
          >
            {t(`${p}.s6.contactDpoLink`)}
          </button>
          {' '}{t(`${p}.s6.exerciseOr`)}{' '}
          <a href="mailto:privacy@defineinfinity.com" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300">
            privacy@defineinfinity.com
          </a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s7.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s7.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s8.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s8.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s8.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s8.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s9.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s9.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s9.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s9.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s10.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s10.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s11.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s11.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s12.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s12.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s13.title`)}</h2>
        <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-300 dark:border-violet-500/30 rounded-lg p-6">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-2">
            <strong className="text-gray-900 dark:text-white">{t(`${p}.s13.dpoLabel`)}</strong>
          </p>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t(`${p}.s13.emailLabel`)} <a href="mailto:privacy@defineinfinity.com" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300">privacy@defineinfinity.com</a><br />
            {t(`${p}.s13.addressLine`)}<br />
            {t(`${p}.s13.euRepLine`)}
          </p>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s13.complaintNote`)}</p>
        </div>
      </section>
    </LegalPageLayout>
  );
};

// Terms of Service Page
export const TermsPage = () => {
  const { t } = useTranslation();
  const p = 'legal.terms';

  return (
    <LegalPageLayout title={t(`${p}.pageTitle`)} icon={FileText}>
      <Helmet>
        <title>Terms of Service | Stocker Beta</title>
        <meta name="description" content="The terms governing use of Stocker: account eligibility, acceptable use, content moderation, and governing law." />
        <link rel="canonical" href="https://stocker-beta.com/terms" />
      </Helmet>
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s1.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s1.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s2.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s2.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s2.items`, { returnObjects: true })} />
        </ul>
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-orange-600 dark:border-yellow-500 p-4 mt-6">
          <p className="font-semibold text-yellow-700 dark:text-yellow-400 mb-2">{t(`${p}.s2.noticeLabel`)}</p>
          <p className="text-gray-600 dark:text-gray-300">
            <strong className="text-gray-900 dark:text-white">{t(`${p}.s2.noticeBold`)}</strong> {t(`${p}.s2.noticeRest`)}
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s3.title`)}</h2>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-6">{t(`${p}.s3.creation.title`)}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s3.creation.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s3.creation.items`, { returnObjects: true })} />
        </ul>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-6">{t(`${p}.s3.eligibility.title`)}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s3.eligibility.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s4.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s4.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s4.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s4.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s5.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s5.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s5.items`, { returnObjects: true })} />
        </ul>
        <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 mt-6">
          <p className="text-gray-600 dark:text-gray-300">
            <strong className="text-gray-900 dark:text-white">{t(`${p}.s5.violationBold`)}</strong> {t(`${p}.s5.violationRest`)}
          </p>
        </div>

        <h3 className="text-xl font-semibold text-violet-600 dark:text-violet-400 mb-3 mt-8">{t(`${p}.s5.moderation.title`)}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s5.moderation.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s5.moderation.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s5.moderation.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s6.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s6.body1`)}</p>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s6.body2`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s7.title`)}</h2>
        <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s7.body`)}</p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s8.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s8.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s8.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s9.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s9.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s10.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s10.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s10.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s10.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s11.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s11.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s12.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s12.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s13.title`)}</h2>
        <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-300 dark:border-violet-500/30 rounded-lg p-6">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t(`${p}.s13.forQuestions`)}<br />
            {t(`${p}.s13.emailLabel`)} <a href="mailto:legal@defineinfinity.com" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300">legal@defineinfinity.com</a><br />
            {t(`${p}.s13.addressLine`)}
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
};

// Disclaimer Page
export const DisclaimerPage = () => {
  const { t } = useTranslation();
  const p = 'legal.disclaimer';

  return (
    <LegalPageLayout title={t(`${p}.pageTitle`)} icon={AlertTriangle} lastUpdated={t(`${p}.lastUpdatedDate`)}>
      <Helmet>
        <title>Disclaimer | Stocker Beta</title>
        <meta name="description" content="Stocker is not investment advice: what the platform's data, sentiment, and credibility scores are and are not, and the risks of relying on them." />
        <link rel="canonical" href="https://stocker-beta.com/disclaimer" />
      </Helmet>
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-orange-600 dark:border-yellow-500 p-6 mb-8">
        <p className="font-bold text-yellow-700 dark:text-yellow-400 text-xl mb-2">{t(`${p}.banner.title`)}</p>
        <p className="text-gray-600 dark:text-gray-300">{t(`${p}.banner.body`)}</p>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s1.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s1.intro`)}</p>
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-500 rounded-lg p-6">
          <p className="font-bold text-red-700 dark:text-red-400 mb-2">{t(`${p}.s1.noticeLabel`)}</p>
          <p className="text-gray-600 dark:text-gray-300">{t(`${p}.s1.noticeBody`)}</p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s2.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s2.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s2.items`, { returnObjects: true })} />
        </ul>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">{t(`${p}.s2.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s3.title`)}</h2>
        <div className="bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500 p-6 mb-4">
          <p className="font-semibold text-orange-700 dark:text-orange-400 mb-2">{t(`${p}.s3.warningLabel`)}</p>
          <p className="text-gray-600 dark:text-gray-300">{t(`${p}.s3.warningBody`)}</p>
        </div>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s3.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s3.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s4.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          <strong className="text-gray-900 dark:text-white">{t(`${p}.s4.introBold`)}</strong> {t(`${p}.s4.introRest`)}
        </p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s4.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s5.title`)}</h2>
        <div className="bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500 p-6 mb-4">
          <p className="font-semibold text-orange-700 dark:text-orange-400 mb-2">{t(`${p}.s5.warningLabel`)}</p>
          <p className="text-gray-600 dark:text-gray-300">{t(`${p}.s5.warningBody`)}</p>
        </div>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s5.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <LabeledItems items={t(`${p}.s5.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s6.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s6.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 mb-4">
          <PlainItems items={t(`${p}.s6.items`, { returnObjects: true })} />
        </ul>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-500/40 rounded-lg p-6 mb-4">
          <p className="text-gray-600 dark:text-gray-300">
            <strong className="text-gray-900 dark:text-white">{t(`${p}.s6.disputeIntro`)}</strong> {t(`${p}.s6.disputeBody`)}
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s6.outro`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s7.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s7.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s8.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s8.body1`)}</p>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s8.body2`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s9.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s9.body1`)}</p>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s9.body2`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s10.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s10.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s11.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s11.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s11.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s12.title`)}</h2>
        <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-300 dark:border-violet-500/30 rounded-lg p-6">
          <p className="font-bold text-gray-900 dark:text-white mb-2">{t(`${p}.s12.label`)}</p>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s12.body`)}</p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s13.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s13.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s14.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t(`${p}.s14.body`)}</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s15.title`)}</h2>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t(`${p}.s15.intro`)}</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
          <PlainItems items={t(`${p}.s15.items`, { returnObjects: true })} />
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s16.title`)}</h2>
        <div className="bg-violet-50 dark:bg-violet-500/20 border-l-4 border-violet-500 p-6">
          <p className="font-bold text-gray-900 dark:text-white mb-4 text-lg">{t(`${p}.s16.intro`)}</p>
          <ul className="space-y-3 text-gray-600 dark:text-gray-300">
            {t(`${p}.s16.items`, { returnObjects: true }).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-violet-600 dark:text-violet-400 flex-shrink-0">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t(`${p}.s17.title`)}</h2>
        <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-300 dark:border-violet-500/30 rounded-lg p-6">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t(`${p}.s17.forQuestions`)}<br />
            {t(`${p}.s17.emailLabel`)} <a href="mailto:legal@defineinfinity.com" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300">legal@defineinfinity.com</a><br />
            {t(`${p}.s17.addressLine`)}
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
};

const LegalPages = { PrivacyPage, TermsPage, DisclaimerPage };
export default LegalPages;
